import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
} from '@mui/material';
import { fetchViewFieldCatalog, type ViewFieldCatalogEntry } from 'api/contacts';
import useLocalization from 'hooks/useLocalization';
import React from 'react';
import { enqueueSnackbar } from 'notistack';
import { store, useSelector, dispatch } from 'store';
import { updateUserConfig } from 'store/slices/userConfig';
import {
  createCompanyCustomView,
  updateCompanyCustomView,
  extractCustomViewsError,
} from 'api/custom-views';
import { ChatUserModel } from 'types/chat-users/chat-user';
import { ViewCondition, FilterGroup } from 'types/userConfig/userConfig';
import { generateUniqueId, safeParseJSON } from 'utils/helpers';
import Grid from '@mui/material/Grid2';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import CreateCustomViewConditions from './CreateCustomViewConditions';
import CreateCustomViewFilterGroups from './CreateCustomViewFilterGroups';
import { useLocation, useNavigate } from 'react-router-dom';
import { parseCustomChatViewIdFromPathname } from 'utils/chat-path-params';
import { migrateCustomView, isFilterGroup } from 'utils/migrate-custom-views';
import { useChatSidebarLayoutOptional } from 'contexts/ChatSidebarLayoutContext';
import { WORKFLOW_STATUS_FILTER_OPTIONS } from 'types/workflow/workflow-status';
import {
  getLockedFolderFilterPreset,
  isLockedFolderFilterPreset,
} from 'utils/chat-sidebar-folders-storage';

interface CreateCustomViewProps {
  open: boolean;
  onClose: () => void;
  createOrEdit: 'create' | 'edit' | null;
  /** התיקייה שאליה תשויך התצוגה החדשה (אחסון מקומי בלבד — TODO(DB)) */
  targetFolderId?: string;
}
export interface CreateCustomViewRefProps {
  setData: (id: string | number | null, name: string, conditions: any[]) => void;
  clearData: () => void;
}
/** רשומה באוצר תגיות איש הקשר (ContactTagDefinitions) — צורה מינימלית שהבורר צריך */
type ContactTagVocabularyEntry = { id: number | string; name: string; isActive?: boolean };
const EMPTY_CONTACT_TAGS: ContactTagVocabularyEntry[] = [];

export type ViewFilterOptions = {
  key: keyof ChatUserModel | string; // מאפשר גם שדות שלא קיימים ב-ChatUserModel (כמו IsOpenUpdateTimeFrom)
  label: string | null;
  type: 'input' | 'number' | 'select' | 'dateTime' | 'checkbox';
  options?: Array<{ value: any; label: string | null }>;
  multiple?: boolean;
  operators?: Array<'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains' | 'startswith' | 'endswith' | 'in' | 'notin' | 'isnull' | 'isnotnull'>;
  category?: 'contact' | 'lastSession' | 'lastMessageObject' | 'lastIncomingMessage' | 'lastOutgoingMessage'; // קטגוריה
  categoryLabel?: string; // שם הקטגוריה בעברית (למשל "איש קשר", "שיחה", "שיחה אחרונה")
};

const CreateCustomView = React.forwardRef<CreateCustomViewRefProps, CreateCustomViewProps>(
  ({ open, onClose, createOrEdit, targetFolderId }, ref) => {
    //* localization
    const { currentLocale } = useLocalization();
    //* שיוך תצוגה→תיקייה (אחסון מקומי בלבד — TODO(DB))
    const chatSidebarLayout = useChatSidebarLayoutOptional();
    const localization = React.useMemo(
      () => currentLocale.settingsPopup.navigation.filters.create,
      [currentLocale],
    );

    //* Assignees
    const applicationUsers = useSelector((state) => state.applicationUsers);
    const selectableAssignees = React.useMemo(() => {
      return applicationUsers
        ?.filter((user) => {
          const userProfileConfig = safeParseJSON(user.ProfileConfig);

          const visible =
            userProfileConfig?.funnerInfo?.visibleInList !== undefined &&
            userProfileConfig?.funnerInfo?.visibleInList !== null
              ? userProfileConfig.funnerInfo.visibleInList
              : user.IsActive;

          return visible;
        })
        .filter((user) => user.FullName !== null && user.FullName !== undefined);
    }, [applicationUsers]);

    //* labels
    const chatUsersLabelsList = useSelector((state) => state.labels);

    //* תגיות איש קשר — אוצר נפרד מתגיות השיחה; ה-slice נוסף עם מסך הניהול (GET /api/contactTags).
    //* עד אז הבורר נופל לקלט חופשי. selector יציב (קבוע ריק) כדי לא לייצר רינדור על כל שינוי ב-store.
    const contactTagsVocabulary = useSelector(
      (state) =>
        (state as unknown as { contactTags?: { items?: ContactTagVocabularyEntry[] } }).contactTags?.items ??
        EMPTY_CONTACT_TAGS,
    );

    /**
     * מקור הפקדים והערכים בלבד. *אילו* שדות קיימים, שמותיהם, קיבוצם
     * והאופרטורים שלהם מגיעים מהשרת (fetchViewFieldCatalog) — ראה filterOptions
     * מתחת. הרשימה כאן תורמת רק את סוג הפקד, multiple וערכי האפשרויות, שהם
     * נתוני חברה ולא סכמה; היא גם רשת הביטחון אם הקריאה לשרת נכשלת.
     * אין להוסיף כאן שדות חדשים — שדה נוסף מתווסף בקטלוג שבשרת.
     */
    const localFieldUi: Array<ViewFilterOptions> = React.useMemo(
      () => [
        {
          key: 'firstName',
          label: 'שם פרטי',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'lastName',
          label: 'שם משפחה',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'fullName',
          label: 'שם מלא',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'phone',
          label: 'טלפון',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'email',
          label: 'אימייל',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'utmSource',
          label: 'UTM מקור',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'utmMedium',
          label: 'UTM מדיום',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'utmCampaign',
          label: 'UTM קמפיין',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'utmTerm',
          label: 'UTM מונח',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'utmContent',
          label: 'UTM תוכן',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          // מנהל השיחה — מי מטפל בשיחה הנוכחית (Sessions.manager_id). שדה של
          // השיחה האחרונה ולא של איש הקשר (הוחלט 27.10); הקטלוג בשרת מסווג אותו
          // תחת "שיחה אחרונה", וכאן מיושר לאותה קבוצה לנסיגה בלי שרת.
          key: 'managerId',
          label: 'מנהל שיחה',
          type: 'select',
          multiple: true,
          category: 'lastSession',
          categoryLabel: 'שיחה אחרונה',
          options: selectableAssignees.map((user) => ({
            value: user.Udid,
            label: user.FullName,
          })),
        },
        {
          // מנהל הלקוח — שיוך עצמאי של איש הקשר (ChatUsers.CustomerManagerUdid).
          // ריק = הסופר אדמין של החברה, והשרת מחשב זאת בסינון (COALESCE), ולכן
          // הבורר מציג רק את רשימת הנציגים.
          key: 'customerManagerUdid',
          label: 'מנהל לקוח',
          type: 'select',
          multiple: true,
          category: 'contact',
          categoryLabel: 'איש קשר',
          options: selectableAssignees.map((user) => ({
            value: user.Udid,
            label: user.FullName,
          })),
        },
        {
          // תגיות איש קשר — אוצר נפרד מתגיות השיחה (ContactTagDefinitions, הוחלט 27.10).
          // הערך הנשמר הוא שם התגית, כמו בתגית שיחה; האפשרויות מגיעות מ-slice
          // contactTags כשהוא קיים (נטען מ-GET /api/contactTags), אחרת קלט חופשי.
          key: 'contactTag',
          label: 'תגיות איש קשר',
          type: contactTagsVocabulary.length ? 'select' : 'input',
          multiple: false,
          category: 'contact',
          categoryLabel: 'איש קשר',
          options: contactTagsVocabulary
            .filter((tag) => tag.isActive !== false)
            .map((tag) => ({ value: tag.name, label: tag.name })),
        },
        {
          // תגית שיחה — אוצר התגיות הרשמי של החברה (OwnerCompany.ChatUserLabels),
          // אותה רשימה שמזינה את בורר התוויות. הערך הנשמר הוא *שם* התגית, כי
          // SessionConversationTag.tag הוא טקסט — להבדיל מ-LableName ששמר JSON.
          // שדה של השיחה האחרונה (קבוצה "שיחה אחרונה"), כמו בקטלוג שבשרת.
          key: 'conversationTag',
          label: 'תגית שיחה',
          type: 'select',
          // בחירה יחידה: in/notin לא מוצעים לשדה הזה (האגרגט היה מטעה בשיחה
          // עם כמה תגיות), ולכן ריבוי ערכים היה מייצר תנאי שאי אפשר לנסח
          multiple: false,
          category: 'lastSession',
          categoryLabel: 'שיחה אחרונה',
          options:
            chatUsersLabelsList?.map((label) => ({
              value: label.name,
              label: label.name,
            })) || [],
        },

        {
          key: 'city',
          label: 'עיר',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'address',
          label: 'רחוב',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        {
          key: 'gender',
          label: 'מגדר',
          type: 'select',
          category: 'contact',
          categoryLabel: 'איש קשר',
          options: [
            { value: 'male', label: 'זכר' },
            { value: 'female', label: 'נקבה' },
          ],
        },
        {
          key: 'birthDate',
          label: 'תאריך לידה',
          type: 'input',
          category: 'contact',
          categoryLabel: 'איש קשר',
        },
        // ========== קטגוריה: שיחה אחרונה (lastSession) ==========
        {
          // סטטוס הפלואו של השיחה (1-6) — החליף את open/closed (הוחלט 15.10)
          key: 'lastSession.flowStatusCode',
          label: 'סטטוס',
          type: 'select',
          multiple: true,
          category: 'lastSession',
          categoryLabel: 'שיחה אחרונה',
          options: WORKFLOW_STATUS_FILTER_OPTIONS,
        },
        {
          key: 'lastSession.dateStart',
          label: 'תאריך התחלה',
          type: 'dateTime',
          category: 'lastSession',
          categoryLabel: 'שיחה אחרונה',
        },
        {
          key: 'lastSession.dateEnd',
          label: 'תאריך סיום',
          type: 'dateTime',
          category: 'lastSession',
          categoryLabel: 'שיחה אחרונה',
        },
        {
          key: 'lastSession.durationMinutes',
          label: 'משך בדקות',
          type: 'number',
          category: 'lastSession',
          categoryLabel: 'שיחה אחרונה',
        },
        {
          key: 'lastSession.unreadCount',
          label: 'מספר הודעות שלא נקראו',
          type: 'number',
          category: 'lastSession',
          categoryLabel: 'שיחה אחרונה',
        },
        // "משויך" (lastSession.manager.id) אוחד לתוך managerId למעלה — תצוגות
        // שמורות עם המפתח הישן ממופות ל-managerId ב-field-mapping.
        // ========== קטגוריה: הודעה אחרונה (lastMessageObject) ==========
        {
          key: 'lastMessageObject.dateCreated',
          label: 'תאריך ושעה',
          type: 'dateTime',
          category: 'lastMessageObject',
          categoryLabel: 'הודעה אחרונה',
        },
        {
          key: 'lastMessageObject.phone',
          label: 'טלפון',
          type: 'input',
          category: 'lastMessageObject',
          categoryLabel: 'הודעה אחרונה',
        },
        {
          key: 'lastMessageObject.status',
          label: 'סטטוס',
          type: 'select',
          category: 'lastMessageObject',
          categoryLabel: 'הודעה אחרונה',
          options: [
            { value: 'received', label: 'התקבלה' },
            { value: 'read', label: 'נקראה' },
            { value: 'delivered', label: 'נמסרה' },
            { value: 'sent', label: 'נשלחה' },
          ],
        },
        {
          // "תבנית" כאפשרות נוספת בסוג התוכן (FileType='template') — הוחלט 14.10
          key: 'lastMessageObject.type',
          label: 'סוג התוכן',
          type: 'select',
          category: 'lastMessageObject',
          categoryLabel: 'הודעה אחרונה',
          options: [
            { value: 'text', label: 'טקסט' },
            { value: 'template', label: 'תבנית' },
            { value: 'image', label: 'תמונה' },
            { value: 'video', label: 'וידאו' },
            { value: 'audio', label: 'אודיו' },
            { value: 'document', label: 'מסמך' },
          ],
        },
        {
          key: 'lastMessageObject.direction',
          label: 'כיוון',
          type: 'select',
          category: 'lastMessageObject',
          categoryLabel: 'הודעה אחרונה',
          options: [
            { value: 'incoming', label: 'נכנסת' },
            { value: 'outgoing', label: 'יוצאת' },
          ],
        },
        {
          // send_source בטבלת ההודעות; היסטוריה ישנה NULL — isnull יתפוס "לא ידוע"
          key: 'lastMessageObject.sendSource',
          label: 'מקור הודעה',
          type: 'select',
          category: 'lastMessageObject',
          categoryLabel: 'הודעה אחרונה',
          options: [
            { value: 'api', label: 'API' },
            { value: 'broadcast', label: 'תפוצה' },
            { value: 'front', label: 'ממשק משתמש' },
            { value: 'bot', label: 'בוט' },
            { value: 'make', label: 'אוטומציה (Make)' },
          ],
        },
        {
          key: 'lastMessageObject.globalUserId',
          label: 'משתמש',
          type: 'select',
          multiple: true,
          category: 'lastMessageObject',
          categoryLabel: 'הודעה אחרונה',
          options: selectableAssignees.map((user) => ({
            value: user.Udid,
            label: user.FullName,
          })),
        },
        // ========== קטגוריה: הודעה נכנסת אחרונה (lastIncomingMessage) ==========
        // בלי "כיוון" (תמיד נכנסת), בלי "מקור" ובלי "משתמש" — הוחלט 14.10
        {
          key: 'lastIncomingMessage.dateCreated',
          label: 'תאריך ושעה',
          type: 'dateTime',
          category: 'lastIncomingMessage',
          categoryLabel: 'הודעה נכנסת אחרונה',
        },
        {
          key: 'lastIncomingMessage.phone',
          label: 'טלפון',
          type: 'input',
          category: 'lastIncomingMessage',
          categoryLabel: 'הודעה נכנסת אחרונה',
        },
        {
          key: 'lastIncomingMessage.status',
          label: 'סטטוס',
          type: 'select',
          category: 'lastIncomingMessage',
          categoryLabel: 'הודעה נכנסת אחרונה',
          options: [
            { value: 'received', label: 'התקבלה' },
            { value: 'read', label: 'נקראה' },
          ],
        },
        {
          key: 'lastIncomingMessage.type',
          label: 'סוג התוכן',
          type: 'select',
          category: 'lastIncomingMessage',
          categoryLabel: 'הודעה נכנסת אחרונה',
          options: [
            { value: 'text', label: 'טקסט' },
            { value: 'template', label: 'תבנית' },
            { value: 'image', label: 'תמונה' },
            { value: 'video', label: 'וידאו' },
            { value: 'audio', label: 'אודיו' },
            { value: 'document', label: 'מסמך' },
          ],
        },
        // ========== קטגוריה: הודעה יוצאת אחרונה (lastOutgoingMessage) ==========
        // בלי "כיוון" (תמיד יוצאת) — הוחלט 14.10
        {
          key: 'lastOutgoingMessage.dateCreated',
          label: 'תאריך ושעה',
          type: 'dateTime',
          category: 'lastOutgoingMessage',
          categoryLabel: 'הודעה יוצאת אחרונה',
        },
        {
          key: 'lastOutgoingMessage.phone',
          label: 'טלפון',
          type: 'input',
          category: 'lastOutgoingMessage',
          categoryLabel: 'הודעה יוצאת אחרונה',
        },
        {
          key: 'lastOutgoingMessage.status',
          label: 'סטטוס',
          type: 'select',
          category: 'lastOutgoingMessage',
          categoryLabel: 'הודעה יוצאת אחרונה',
          options: [
            { value: 'sent', label: 'נשלחה' },
            { value: 'delivered', label: 'נמסרה' },
            { value: 'read', label: 'נקראה' },
          ],
        },
        {
          key: 'lastOutgoingMessage.type',
          label: 'סוג התוכן',
          type: 'select',
          category: 'lastOutgoingMessage',
          categoryLabel: 'הודעה יוצאת אחרונה',
          options: [
            { value: 'text', label: 'טקסט' },
            { value: 'template', label: 'תבנית' },
            { value: 'image', label: 'תמונה' },
            { value: 'video', label: 'וידאו' },
            { value: 'audio', label: 'אודיו' },
            { value: 'document', label: 'מסמך' },
          ],
        },
        {
          key: 'lastOutgoingMessage.sendSource',
          label: 'מקור הודעה',
          type: 'select',
          category: 'lastOutgoingMessage',
          categoryLabel: 'הודעה יוצאת אחרונה',
          options: [
            { value: 'api', label: 'API' },
            { value: 'broadcast', label: 'תפוצה' },
            { value: 'front', label: 'ממשק משתמש' },
            { value: 'bot', label: 'בוט' },
            { value: 'make', label: 'אוטומציה (Make)' },
          ],
        },
        {
          key: 'lastOutgoingMessage.globalUserId',
          label: 'משתמש',
          type: 'select',
          multiple: true,
          category: 'lastOutgoingMessage',
          categoryLabel: 'הודעה יוצאת אחרונה',
          options: selectableAssignees.map((user) => ({
            value: user.Udid,
            label: user.FullName,
          })),
        },
      ],
      [chatUsersLabelsList, contactTagsVocabulary, selectableAssignees],
    );

    /** קטלוג השדות מהשרת — מקור האמת לאילו שדות קיימים ואיך הם נקראים */
    const [serverFieldCatalog, setServerFieldCatalog] = React.useState<ViewFieldCatalogEntry[] | null>(null);
    React.useEffect(() => {
      let cancelled = false;
      fetchViewFieldCatalog().then((fields) => {
        if (!cancelled && fields) setServerFieldCatalog(fields);
      });
      return () => {
        cancelled = true;
      };
    }, []);

    /**
     * הרשימה שהמסך עובד איתה: השדות, התוויות, הקיבוץ והאופרטורים מהשרת,
     * והפקד וערכי האפשרויות מהמפה המקומית. שדה שהשרת מחזיר ואין לו פקד
     * מוגדר מקבל פקד לפי הטיפוס שלו.
     */
    const filterOptions: Array<ViewFilterOptions> = React.useMemo(() => {
      if (!serverFieldCatalog?.length) return localFieldUi;

      const uiByKey = new Map(localFieldUi.map((o) => [String(o.key), o]));
      const controlForType = (t: ViewFieldCatalogEntry['type']): ViewFilterOptions['type'] => {
        if (t === 'date') return 'dateTime';
        if (t === 'number') return 'number';
        return 'input';
      };

      return serverFieldCatalog.map((field) => {
        const ui = uiByKey.get(field.key);
        return {
          ...(ui ?? {}),
          key: field.key,
          label: field.label,
          category: field.category,
          categoryLabel: field.categoryLabel,
          operators: field.operators,
          type: ui?.type ?? controlForType(field.type),
        } as ViewFilterOptions;
      });
    }, [serverFieldCatalog, localFieldUi]);

    const location = useLocation();
    const viewId = React.useMemo(() => parseCustomChatViewIdFromPathname(location.pathname), [location.pathname]);
    const navigate = useNavigate();

    const [filtersId, setFiltersId] = React.useState<string | number | null>(null);
    const [filtersName, setFiltersName] = React.useState<string>('');
    const [filtersConditions, setFiltersConditions] = React.useState<ViewCondition[]>([]); // למבנה ישן
    const [filterGroups, setFilterGroups] = React.useState<FilterGroup[]>([]); // למבנה חדש
    const [filtersLogic, setFiltersLogic] = React.useState<'or' | 'and'>('and');
    const [useNewStructure, setUseNewStructure] = React.useState<boolean>(true); // דגל לשימוש במבנה החדש

    React.useImperativeHandle(ref, () => ({
      setData,
      clearData,
    }));

    const setData = React.useCallback(
      (id: string | number | null, name: string, conditions: any[]) => {
        setFiltersId(id);
        setFiltersName(name);
        
        // בדיקה אם זה מבנה חדש (FilterGroup[]) או ישן (ViewCondition[])
        if (conditions.length > 0 && isFilterGroup(conditions[0])) {
          setUseNewStructure(true);
          setFilterGroups(conditions as FilterGroup[]);
          setFiltersConditions([]);
        } else {
          // המרת מבנה ישן למבנה חדש
          const migrated = migrateCustomView({
            id: id || '',
            name,
            filters: conditions,
          });
          setUseNewStructure(true);
          setFilterGroups(migrated.filters as FilterGroup[]);
          setFiltersLogic(migrated.filtersLogic || 'and');
          setFiltersConditions([]);
        }
      },
      [],
    );
    const clearData = React.useCallback(() => {
      setFiltersId(null);
      setFiltersName('');
      setFiltersConditions([]);
      setFilterGroups([]);
      setFiltersLogic('and');
      setUseNewStructure(true);
    }, []);

    const lockedFolderFilters = React.useMemo(
      () => createOrEdit === 'create' && isLockedFolderFilterPreset(targetFolderId),
      [createOrEdit, targetFolderId],
    );

    React.useEffect(() => {
      if (!open || createOrEdit !== 'create') return;
      const preset = getLockedFolderFilterPreset(targetFolderId);
      if (preset) {
        setFilterGroups(preset);
        setFiltersLogic('and');
        setUseNewStructure(true);
        setFiltersConditions([]);
        return;
      }
      setFilterGroups([]);
      setFiltersLogic('and');
      setFiltersConditions([]);
    }, [open, createOrEdit, targetFolderId]);

    const handleClose = () => {
      onClose();
      setTimeout(() => {
        clearData();
      }, 300);
    };

    const handleFiltersName = (value: string) => {
      setFiltersName(value);
    };
    //* Conditions handlers
    const addNewCondition = () => {
      const newConditionObj = {
        id: generateUniqueId(
          filtersConditions.map((cond) => cond.id.toString()),
          5,
        ),
        key: '' as keyof ChatUserModel,
        value: null,
      };

      setFiltersConditions((prevList) => [...prevList, newConditionObj]);
    };
    const deleteCondition = (id: string | number) => {
      setFiltersConditions((prevList) => prevList.filter((cond) => cond.id !== id));
    };
    const handleConditionParams = (id: string | number, params: Partial<ViewCondition>) => {
      setFiltersConditions((prevList) => {
        const newList = [...prevList];
        const targetConditionIndex = newList.findIndex((cond) => cond.id === id);
        newList[targetConditionIndex] = {
          ...newList[targetConditionIndex],
          ...params,
        };
        return newList;
      });
    };

    // שמירה לטבלת CompanyCustomViews בנוד (מזהה נוצר בשרת); ה-store מתעדכן
    // מיידית מהתשובה — getChatUsers קורא customViews בזמן הניווט
    const saveCurrentFilters = async (id: string | number | null = null) => {
      handleClose();
      const storeState = store.getState();
      const currentViews = [...storeState.userConfig.customViews];

      // שימוש במבנה החדש
      const filtersToSave = useNewStructure && filterGroups.length > 0
        ? filterGroups
        : filtersConditions; // fallback למבנה ישן
      const logicToSave = useNewStructure && filterGroups.length > 0 ? filtersLogic : undefined;

      try {
        if (createOrEdit === 'edit' && id != null) {
          const existingView = currentViews.find((view) => view.id === id);
          if (!existingView) return;
          const updated = await updateCompanyCustomView(String(id), {
            name: filtersName,
            filters: filtersToSave,
            filtersLogic: logicToSave,
          });
          dispatch(
            updateUserConfig({
              customViews: currentViews.map((view) => (view.id === id ? { ...view, ...updated } : view)),
            }),
          );
          enqueueSnackbar('התצוגה עודכנה בהצלחה', { variant: 'success' });
          if (viewId === String(id)) {
            navigate(`/chats/${storeState.chat.selectedChatUser?.UserDisplayPhoneNumber || ''}`);
          }
        } else {
          const created = await createCompanyCustomView({
            name: filtersName,
            filters: filtersToSave,
            filtersLogic: logicToSave,
          });
          dispatch(updateUserConfig({ customViews: [...currentViews, created] }));

          // שיוך התצוגה החדשה לתיקייה שנבחרה (שיוך לתיקיות הוא פר-משתמש)
          if (targetFolderId) {
            chatSidebarLayout?.assignViewToFolder(String(created.id), targetFolderId);
          }
          enqueueSnackbar(`התצוגה "${created.name}" נוצרה בהצלחה`, { variant: 'success' });
          navigate(`/chats/custom/${created.id}`);
        }
      } catch (error) {
        enqueueSnackbar(extractCustomViewsError(error, 'שגיאה בשמירת התצוגה'), { variant: 'error' });
      }
    };

    const buttonDisabled = React.useMemo(() => {
      if (filtersName === '') return true;
      if (useNewStructure) {
        // בדיקה שיש לפחות קבוצה אחת עם לפחות תנאי אחד תקין
        if (filterGroups.length === 0) return true;
        
        // בדיקה שיש לפחות קבוצה אחת עם לפחות תנאי אחד תקין
        const hasValidGroup = filterGroups.some((group) => {
          if (group.conditions.length === 0) return false;
          // בדיקה שכל התנאים תקינים (יש field, operator, ו-value אם נדרש)
          return group.conditions.some((cond) => {
            if (!cond.field || !cond.operator) return false;
            // עבור isnull/isnotnull לא צריך value
            if (cond.operator === 'isnull' || cond.operator === 'isnotnull') return true;
            // עבור שאר ה-operators צריך value
            return cond.value !== undefined && cond.value !== null && cond.value !== '';
          });
        });
        
        return !hasValidGroup;
      }
      return filtersConditions.length === 0;
    }, [filtersConditions.length, filterGroups, filtersName, useNewStructure]);

    return (
      <Dialog 
        onClose={handleClose} 
        open={open} 
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            //* הורחב מ-520 — שורת תנאי עם ערך דינמי (שדה, אופרטור, ב-X, מספר, יחידות)
            //* נחתכה; ברוחב הזה כל השדות נכנסים בשורה אחת בלי חיתוך.
            maxWidth: 840,
            width: '100%',
          },
        }}
      >
        <DialogTitle>
          <Grid container spacing={2} alignItems={'center'}>
            <Grid size="grow">{localization.title}</Grid>
            <Grid size="auto">
              <IconButton onClick={handleClose}>
                <CloseOutlinedIcon />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>

        <DialogContent
          sx={{
            pt: '12px !important',
            px: { xs: 2, sm: 3 },
            pb: 2,
          }}
        >
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                fullWidth
                size="small"
                label={localization.nameLabel}
                value={filtersName}
                onChange={(e) => handleFiltersName(e.target.value)}
              />
            </Grid>
            <Grid size={12} sx={{ py: 0 }}>
              <Divider sx={{ borderColor: 'divider' }} />
            </Grid>
            <Grid size={12} sx={{ pt: 0 }}>
              {useNewStructure ? (
                <CreateCustomViewFilterGroups
                  options={filterOptions}
                  filterGroups={filterGroups}
                  filtersLogic={filtersLogic}
                  onFilterGroupsChange={setFilterGroups}
                  onFiltersLogicChange={setFiltersLogic}
                  readOnly={lockedFolderFilters}
                />
              ) : (
                <CreateCustomViewConditions
                  options={filterOptions}
                  conditions={filtersConditions}
                  addNewCondition={addNewCondition}
                  deleteCondition={deleteCondition}
                  onChange={handleConditionParams}
                />
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            gap: 1.5,
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Button variant="outlined" color="error" onClick={handleClose}>
            {localization.close}
          </Button>
          <Button variant="contained" onClick={() => saveCurrentFilters(filtersId)} disabled={buttonDisabled}>
            {localization.save}
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

export default CreateCustomView;
